import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useHashScroll from '../hooks/useHashScroll';
import { useContent } from '../contexts/ContentContext';
import { getExpertAnchorId } from '../utils/slug';
import {
  getSpecialties,
  getCoursesForSpecialty,
  getCoursePath,
  getSpecialtyPath,
} from '../utils/formationCatalog';
import { trackContact } from '../utils/metaPixel';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const Navigation = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { scrollToElement } = useHashScroll();
  const { getContent } = useContent();
  const skipScrollRestoreRef = useRef(false);

  // Specialties and courses from CMS
  const specialties = getSpecialties(getContent);

  // Experts from CMS (same source as /experts page)
  const expertsTeam = getContent('experts.team') || {};
  const expertTrainers = expertsTeam.trainers || [];
  const companyName = getContent('global.company')?.name || 'DentoXcellence';
  const consultingWhatsAppUrl = buildWhatsAppUrl(
    'Bonjour, je suis intéressé(e) par vos services de consulting',
    null,
    getContent
  );

  // Handle hamburger menu toggle
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu when clicking on nav links
  const closeMenu = (options = {}) => {
    if (options.skipScrollRestore) {
      skipScrollRestoreRef.current = true;
    }
    setIsMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown((current) => (current === dropdownName ? null : dropdownName));
  };

  const handleNavLinkClick = () => {
    closeMenu({ skipScrollRestore: true });
  };

  const handleDropdownChevronClick = (e, dropdownName) => {
    e.preventDefault();
    e.stopPropagation();
    toggleDropdown(dropdownName);
  };

  // Handle same-page navigation with smooth scrolling
  const handleSamePageNavigation = (e, targetPath, hash) => {
    const currentPath = location.pathname;
    const targetPagePath = targetPath.split('#')[0];
    
    // If we're on the same page and there's a hash, scroll instead of navigate
    if (currentPath === targetPagePath && hash) {
      e.preventDefault();
      closeMenu({ skipScrollRestore: true });
      scrollToElement(hash);
    }
  };

  // Lock page scroll when mobile menu is open (prevents double scrollbar)
  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const scrollY = window.scrollY;
    document.body.classList.add('nav-menu-open');
    document.body.style.top = `-${scrollY}px`;

    return () => {
      document.body.classList.remove('nav-menu-open');
      document.body.style.top = '';
      if (!skipScrollRestoreRef.current) {
        window.scrollTo(0, scrollY);
      }
      skipScrollRestoreRef.current = false;
    };
  }, [isMenuOpen]);

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keep menu and dropdown open after navigation so subsections remain visible
  // Only close when user clicks outside or selects a subsection
  useEffect(() => {
    // Don't auto-close on route change - let user see subsections
    // Menu will close when user clicks a subsection item (via closeMenu)
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  const renderNavDropdownHeader = (label, to, dropdownName, isActive) => (
    <div className="nav-dropdown-header">
      <Link
        to={to}
        className={`nav-link dropdown-toggle ${isActive ? 'active' : ''}`}
        onClick={handleNavLinkClick}
      >
        {label}
      </Link>
      <button
        type="button"
        className="nav-dropdown-chevron"
        aria-label={`Afficher les sous-sections ${label}`}
        aria-expanded={activeDropdown === dropdownName}
        onClick={(e) => handleDropdownChevronClick(e, dropdownName)}
      >
        <i className={`fas fa-chevron-down ${activeDropdown === dropdownName ? 'rotated' : ''}`}></i>
      </button>
    </div>
  );

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
      <div className="nav-container">
        <div className="nav-logo">
          <Link to="/" onClick={() => closeMenu({ skipScrollRestore: true })}>
            <img src="/DentoxcellenceLogo.png" alt={`${companyName} Logo`} className="logo-image" />
          </Link>
        </div>
        <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          {/* Accueil Dropdown */}
          <div className="nav-dropdown">
            {renderNavDropdownHeader('Accueil', '/', 'accueil', location.pathname === '/')}
            <div className={`dropdown-menu ${activeDropdown === 'accueil' ? 'show' : ''}`}>
              <Link to="/#featured-programs" className="dropdown-item" onClick={(e) => { handleSamePageNavigation(e, "/#featured-programs", "featured-programs"); closeMenu({ skipScrollRestore: true }); }}>
                <i className="fas fa-graduation-cap"></i> Prochaines formations
              </Link>
              <Link to="/#participant-testimonials" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#participant-testimonials", "participant-testimonials"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-comments"></i> Témoignages de nos Participants
              </Link>
              <Link to="/#expert-testimonials" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#expert-testimonials", "expert-testimonials"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-user-md"></i> Témoignages de nos Experts
              </Link>
              <Link to="/#features" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#features", "features"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-star"></i> Pourquoi Nous Choisir
              </Link>
              <Link to="/#upcoming-sessions" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#upcoming-sessions", "upcoming-sessions"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-calendar"></i> Calendrier 2026–2027
              </Link>
              <Link to="/#expert-gallery" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/#expert-gallery", "expert-gallery"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-images"></i> Galerie Photo/Vidéo
              </Link>
            </div>
          </div>

          {/* Formations Dropdown — mega menu (many items, multi-column on desktop) */}
          <div className="nav-dropdown nav-dropdown--mega">
            {renderNavDropdownHeader(
              'Formations',
              '/formations',
              'formations',
              location.pathname === '/formations' || location.pathname.startsWith('/formations/')
            )}
            <div className={`dropdown-menu ${activeDropdown === 'formations' ? 'show' : ''}`}>
              {specialties.map((specialty) => {
                const specialtySlug = specialty.slug || specialty.id;
                const courses = getCoursesForSpecialty(getContent, specialty.id);
                return (
                  <React.Fragment key={specialty.id}>
                    <Link
                      to={getSpecialtyPath(specialtySlug)}
                      className="dropdown-item dropdown-item-special"
                      onClick={() => closeMenu({ skipScrollRestore: true })}
                    >
                      <i className={specialty.icon || 'fas fa-graduation-cap'} /> {specialty.title}
                    </Link>
                    {courses.map((course) => (
                      <Link
                        key={course.id}
                        to={getCoursePath(specialtySlug, course.slug)}
                        className="dropdown-item dropdown-item-course"
                        onClick={() => closeMenu({ skipScrollRestore: true })}
                      >
                        <i className="fas fa-book-open" /> {course.title}
                      </Link>
                    ))}
                  </React.Fragment>
                );
              })}
              
              {/* Separator */}
              <div className="dropdown-separator"></div>
              
              {/* Additional links */}
              <Link 
                to="/formations#calendar" 
                className="dropdown-item dropdown-item-special" 
                onClick={(e) => {
                  handleSamePageNavigation(e, "/formations#calendar", "calendar");
                  closeMenu({ skipScrollRestore: true });
                }}
              >
                <i className="fas fa-calendar-alt"></i> Calendrier Global
              </Link>
              <Link 
                to="/formations#registration-form" 
                className="dropdown-item dropdown-item-special" 
                onClick={(e) => {
                  handleSamePageNavigation(e, "/formations#registration-form", "registration-form");
                  closeMenu({ skipScrollRestore: true });
                }}
              >
                <i className="fas fa-user-plus"></i> Demande d'Inscription
              </Link>
            </div>
          </div>

          {/* À propos Dropdown */}
          <div className="nav-dropdown">
            {renderNavDropdownHeader('À propos', '/about', 'about', location.pathname === '/about')}
            <div className={`dropdown-menu ${activeDropdown === 'about' ? 'show' : ''}`}>
              <Link to="/about#company-story" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#company-story", "company-story"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-book-open"></i> Notre Histoire
              </Link>
              <Link to="/about#mission-vision" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#mission-vision", "mission-vision"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-eye"></i> Notre Vision
              </Link>
              <Link to="/about#mission" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#mission", "mission"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-bullseye"></i> Notre Mission
              </Link>
              <Link to="/about#core-values" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#core-values", "core-values"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-heart"></i> Nos Valeurs Fondamentales
              </Link>
              <Link to="/about#historique" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/about#historique", "historique"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-stream"></i> Notre Historique – Chronologie
              </Link>
            </div>
          </div>
          
          {/* Services Dropdown */}
          <div className="nav-dropdown">
            {renderNavDropdownHeader('Services', '/services', 'services', location.pathname === '/services')}
            <div className={`dropdown-menu ${activeDropdown === 'services' ? 'show' : ''}`}>
              <Link to="/formations" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-graduation-cap"></i> Formation
              </Link>
              <Link to="/contact" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-chalkboard-teacher"></i> Séminaires
              </Link>
              <Link to="/formations" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-user-tie"></i> Coaching
              </Link>
              <a href={consultingWhatsAppUrl} className="dropdown-item" onClick={() => { trackContact({ source: 'navigation_menu', label: 'Consulting WhatsApp' }); closeMenu({ skipScrollRestore: true }); }} target="_blank" rel="noopener noreferrer">
                <i className="fas fa-lightbulb"></i> Consulting
              </a>
            </div>
          </div>

          {/* Experts Dropdown — mega menu (many items, multi-column on desktop) */}
          <div className="nav-dropdown nav-dropdown--mega">
            {renderNavDropdownHeader('Experts', '/experts', 'experts', location.pathname === '/experts')}
            <div className={`dropdown-menu ${activeDropdown === 'experts' ? 'show' : ''}`}>
              {expertTrainers.map((trainer, index) => {
                const anchorId = getExpertAnchorId(trainer, index);
                const label = trainer.name || `Expert ${index + 1}`;
                return (
                  <Link
                    key={trainer.id || trainer.slug || anchorId || index}
                    to={`/experts#${anchorId}`}
                    className="dropdown-item"
                    onClick={(e) => {
                      handleSamePageNavigation(e, `/experts#${anchorId}`, anchorId);
                      closeMenu({ skipScrollRestore: true });
                    }}
                  >
                    <i className="fas fa-user-md"></i> {label}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Témoignages Dropdown */}
          <div className="nav-dropdown">
            {renderNavDropdownHeader(
              'Témoignages',
              '/temoignages',
              'temoignages',
              location.pathname === '/temoignages' || (location.pathname === '/' && location.hash.includes('testimonials'))
            )}
            <div className={`dropdown-menu ${activeDropdown === 'temoignages' ? 'show' : ''}`}>
              <Link to="/temoignages" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-quote-left"></i> Tous les Témoignages
              </Link>
              <Link to="/temoignages#expert-testimonials" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-user-tie"></i> Témoignages de nos Experts
              </Link>
              <Link to="/temoignages#participant-testimonials" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-users"></i> Témoignages de nos Participants
              </Link>
              <Link to="/temoignages#submit" className="dropdown-item" onClick={() => closeMenu({ skipScrollRestore: true })}>
                <i className="fas fa-pen"></i> Laisser un témoignage
              </Link>
            </div>
          </div>

          {/* Contact Dropdown */}
          <div className="nav-dropdown">
            {renderNavDropdownHeader('Contact', '/contact', 'contact', location.pathname === '/contact')}
            <div className={`dropdown-menu ${activeDropdown === 'contact' ? 'show' : ''}`}>
              <Link to="/contact#contact-info" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#contact-info", "contact-info"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-info-circle"></i> Informations
              </Link>
              <Link to="/contact#contact-form" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#contact-form", "contact-form"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-envelope"></i> Formulaire
              </Link>
              <Link to="/contact#quick-actions" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#quick-actions", "quick-actions"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-bolt"></i> Actions Rapides
              </Link>
              <Link to="/contact#locations" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#locations", "locations"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-map-marker-alt"></i> Lieux de Formation
              </Link>
              <Link to="/contact#registration" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#registration", "registration"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-user-plus"></i> Inscription
              </Link>
              <Link to="/contact#faq" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#faq", "faq"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-question-circle"></i> FAQ
              </Link>
              <Link to="/contact#locations" className="dropdown-item" onClick={(e) => {handleSamePageNavigation(e, "/contact#locations", "locations"); closeMenu({ skipScrollRestore: true });}}>
                <i className="fas fa-map"></i> Localisation
              </Link>
            </div>
          </div>
          <Link to="/contact#contact-form" className="nav-phone" onClick={(e) => {handleSamePageNavigation(e, "/contact#contact-form", "contact-form"); closeMenu({ skipScrollRestore: true });}}>
            <i className="fas fa-user-plus"></i> Inscription
          </Link>
        </div>
        <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
